/**
 * EventSeriesRow - Collapsed series card for Manage Events page (#1208)
 *
 * Visual design:
 * - Stacked card effect (ghost layers behind main card)
 * - Repeat icon to indicate recurring
 * - Pattern + location + next date + count
 * - Kebab menu for actions
 */

import { useState, useEffect, useRef } from 'react';
import { Repeat, MapPin, MoreVertical, Edit, Eye, XCircle } from 'lucide-react';
import { POST_EVENT_VISIBILITY_BUFFER_SECONDS } from '../lib/datetime';
import { useNowSeconds } from '../hooks/useNowSeconds';
import type { EventSeries } from '../lib/api/events';

interface EventSeriesRowProps {
  series: EventSeries;
  onTap: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

/**
 * Format timestamp to readable date
 */
function formatNextDate(timestamp: number): string {
  const d = new Date(timestamp * 1000);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

export function EventSeriesRow({ series, onTap, onEdit, onCancel }: EventSeriesRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  // #1693: live nowSeconds via useNowSeconds keeps "isPast" current on
  // long-lived list views (60s ticks). #1782 buffer (1h after end) applied
  // inline so the next instance's effective end matches the upcoming/past
  // filter — a long-running instance (started >1h ago, still running)
  // stays out of the past badge. We compute inline instead of calling
  // isPastEvent() so the impure Date.now() read stays out of render
  // (purity rule from #1689).
  const nowSeconds = useNowSeconds();
  const effectiveEnd = series.next_ends_at ?? series.next_occurrence;
  const isPast = effectiveEnd + POST_EVENT_VISIBILITY_BUFFER_SECONDS < nowSeconds;

  return (
    <div className="relative mb-2 overflow-visible">
      {/* Ghost layers for stacked effect - iOS safe with pointer-events: none */}
      <div
        className="absolute left-1 right-1 h-full rounded-xl border border-oro-border bg-oro-surface opacity-30 pointer-events-none"
        style={{ bottom: '-6px', zIndex: -2 }}
      />
      <div
        className="absolute left-1 right-1 h-full rounded-xl border border-oro-border bg-oro-surface opacity-60 pointer-events-none"
        style={{ bottom: '-3px', zIndex: -1 }}
      />

      {/* Main card */}
      <div
        className="relative bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card cursor-pointer hover:bg-oro-surface-alt transition-colors"
        onClick={onTap}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Title with repeat icon */}
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-accent shrink-0" />
              <h3 className="font-medium text-text-primary truncate">
                {series.title}
              </h3>
            </div>

            {/* Pattern + location */}
            <div className="flex items-center gap-2 mt-1 text-sm text-text-secondary">
              <span>{series.recurrence_pattern}</span>
              {series.location_name && (
                <>
                  <span className="text-text-muted">·</span>
                  <MapPin className="w-3 h-3 shrink-0 text-text-muted" />
                  <span className="truncate">{series.location_name}</span>
                </>
              )}
            </div>

            {/* Next date + RSVP + count */}
            <div className="flex items-center gap-2 mt-2 text-sm">
              <span className="text-text-secondary">
                Next: {formatNextDate(series.next_occurrence)}
              </span>
              <span className="text-text-muted">·</span>
              <span className="text-accent">
                {series.next_rsvp_count} going
              </span>
              <span className="text-text-muted">·</span>
              <span className="text-text-muted">
                {series.upcoming_count} upcoming
              </span>
              {isPast && (
                <>
                  <span className="text-text-muted">·</span>
                  <span className="text-xs text-text-muted bg-oro-surface-alt px-2 py-0.5 rounded">
                    Past
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Action Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-oro-surface-alt transition-colors"
              aria-label="Series actions"
            >
              <MoreVertical className="w-5 h-5 text-text-muted" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-12 z-50 w-48 bg-oro-surface border border-oro-border rounded-xl shadow-float py-1 overflow-hidden">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTap();
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-oro-surface-alt transition-colors"
                >
                  <Eye className="w-4 h-4 text-text-muted" />
                  View Series
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-oro-surface-alt transition-colors"
                >
                  <Edit className="w-4 h-4 text-text-muted" />
                  Edit Series
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel();
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-oro-error hover:bg-oro-surface-alt transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Series
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventSeriesRow;
